import React, { useState } from 'react'
import { IonText } from '@ionic/react'
import { ConditionalRootCauseFields, QuestionField } from '../components'
import { Form, Formik } from 'formik'
import {
  SubmitButton,
  FormWideErrorReporter,
  Select,
  RadioGroup,
} from 'formik-components'
import { buildSelectOptions } from 'utils'
import { uniq } from 'lodash'
import * as Types from 'types'
import PropTypes from 'prop-types'
import { buildCheckoutValidationSchema } from '../checkOutFormFields'

const propTypes = {
  fields: PropTypes.arrayOf(Types.CheckoutField),
  initialValues: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

const defaultProps = {
  fields: [],
}

// Renders the two Root Cause / Operable selects wherever the backend places their slot.
function RootCauseSelects() {
  return (
    <>
      <Select
        name="rootCause"
        label="Root Cause"
        placeholder="Select Root Cause"
        options={buildSelectOptions(Types.ROOT_CAUSE_OPTIONS, true)}
        required
        requiredIndicator="*"
      />
      <RadioGroup
        name="operable"
        label="Operational?"
        options={buildSelectOptions(Types.OPERATIONAL_OPTIONS)}
        required
        requiredIndicator="*"
      />
    </>
  )
}

function WorkOrderCheckoutForm({ fields, initialValues, onSubmit }) {
  const [customFormWideError, setCustomFormWideError] = useState(null)
  const [formKeysSkipValidation, setFormKeysSkipValidation] = useState([])

  const handlePhotoUploadAttempt = (fieldName) => {
    setFormKeysSkipValidation(uniq([...formKeysSkipValidation, fieldName]))
  }

  // Belt and suspenders: the backend is the source of truth for the check-out form and
  // always describes at least a work status and notes, so an empty `fields` list is never a
  // real technician check-out payload (e.g. a manager payload that omits checkOutForm, held
  // briefly in the shared store before the show page's on-entry refetch replaces it). Never
  // render a submittable form here -- an empty Formik submit would check the trip out with no
  // status or notes. The show page refetches on view entry, so this resolves itself.
  if (fields.length === 0) {
    return (
      <div className="w-100p">
        <IonText color="medium">Loading the check-out form…</IonText>
      </div>
    )
  }

  // Renders one entry from the ordered `fields` list: either a client-owned root-cause
  // slot or an API-described data field. Keeping this a single ordered walk preserves the
  // exact field order the backend sends.
  const renderField = (field, { values, isSubmitting, setFieldError }) => {
    if (field.type === Types.CHECKOUT_SLOT_TYPE) {
      switch (field.slot) {
        case Types.CHECKOUT_SLOT.ROOT_CAUSE_SELECTS:
          return <RootCauseSelects key={field.slot} />
        case Types.CHECKOUT_SLOT.ROOT_CAUSE_DETAILS:
          return (
            <ConditionalRootCauseFields
              key={field.slot}
              rootCauseSelection={values.rootCause}
              isSubmitting={isSubmitting}
              handlePhotoUploadAttempt={handlePhotoUploadAttempt}
              shouldSetCustomFormWideError={true}
              setCustomFormWideError={setCustomFormWideError}
              setFieldError={setFieldError}
            />
          )
        default:
          return null
      }
    }

    return (
      <QuestionField
        key={field.fieldName}
        question={field}
        isSubmitting={isSubmitting}
        onPhotoUploadAttempt={handlePhotoUploadAttempt}
        setCustomFormWideError={setCustomFormWideError}
        setFieldError={setFieldError}
      />
    )
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={buildCheckoutValidationSchema(
        fields,
        formKeysSkipValidation
      )}
      onSubmit={onSubmit}
      validateOnBlur={false}
    >
      {({ values, isSubmitting, setFieldError }) => {
        return (
          <Form className="trip-form-container w-100p">
            <FormWideErrorReporter
              key={customFormWideError}
              customFormWideError={customFormWideError}
              setCustomFormWideError={setCustomFormWideError}
            />
            {fields.map((field) =>
              renderField(field, { values, isSubmitting, setFieldError })
            )}
            <SubmitButton
              expand="full"
              isSubmitting={isSubmitting}
              showSpinner={true}
            >
              Check Out
            </SubmitButton>
          </Form>
        )
      }}
    </Formik>
  )
}

WorkOrderCheckoutForm.propTypes = propTypes
WorkOrderCheckoutForm.defaultProps = defaultProps

export default React.memo(WorkOrderCheckoutForm)
